import React from "react";
import "./styles/ServicesPage.css";
import { websitePaths } from "../../routes/websitePaths";
import { serviceGalleryDetails } from "../../components/website/data/serviceGalleryData";

const serviceHighlights: Record<string, string[]> = {
  "residential-construction": [
    "Custom planning for homes, villas, and apartments",
    "Structural quality with finish-focused execution",
    "Clear handover for long-term family use",
  ],
  "commercial-construction": [
    "Business-ready layouts for office and retail spaces",
    "Execution planned around movement, display, and use",
    "Reliable quality and coordinated delivery",
  ],
  "industrial-infrastructure": [
    "Durable layouts for warehouses and process sites",
    "Utility planning and practical site coordination",
    "Built for steady operations and long-term performance",
  ],
  "renovation-remodeling": [
    "Upgrades for old spaces with cleaner layouts",
    "Better finish quality and improved functionality",
    "Managed work for homes, offices, and business spaces",
  ],
  "project-management": [
    "Planning support before work begins",
    "Stage-wise supervision during execution",
    "Coordination across teams, vendors, and timelines",
  ],
  "maintenance-support": [
    "Repair work for damaged or worn areas",
    "Practical upkeep for homes and commercial properties",
    "Fast support to restore safety and usability",
  ],
};

const serviceDescriptions: Record<string, string> = {
  "residential-construction":
    "Ideal for customers planning a new home, villa, duplex, or residential block with dependable structural work and clean finishing.",
  "commercial-construction":
    "Best suited for offices, shops, showrooms, and business spaces that need a professional layout and reliable execution quality.",
  "industrial-infrastructure":
    "Designed for warehouses, plants, and industrial support spaces where durability, movement, and utility planning matter most.",
  "renovation-remodeling":
    "A practical solution for customers who want to upgrade old spaces, improve layouts, and refresh the overall look and function.",
  "project-management":
    "Useful for clients who need structured supervision, better team coordination, and stronger control over timelines and execution quality.",
  "maintenance-support":
    "Suitable for ongoing repair, upkeep, and restoration work that helps keep homes, offices, and sites safe and usable.",
};

const commonSteps = [
  {
    step: "01",
    title: "Requirement Discussion",
    description:
      "We understand your requirement, site condition, project goals, and budget expectations before work begins.",
  },
  {
    step: "02",
    title: "Planning And Scope Finalization",
    description:
      "The work scope, timeline, materials, and execution sequence are planned clearly before site activity starts.",
  },
  {
    step: "03",
    title: "Execution With Supervision",
    description:
      "Our team carries out the work with coordination, regular supervision, and stage-wise quality checks.",
  },
  {
    step: "04",
    title: "Review And Handover",
    description:
      "We review the completed work carefully and hand it over once it is ready for practical use.",
  },
];

const ServicesPage: React.FC = () => {
  const services = [
    {
      slug: "residential-construction",
      title: "Residential Construction",
      description:
        "Custom homes, villas, and apartment projects delivered with quality and timeline discipline.",
    },
    {
      slug: "commercial-construction",
      title: "Commercial Construction",
      description:
        "Office, retail, and hospitality projects engineered for performance and long-term value.",
    },
    {
      slug: "industrial-infrastructure",
      title: "Industrial Infrastructure",
      description:
        "Plants, warehouses, and process facilities with robust structural and utility planning.",
    },
    {
      slug: "renovation-remodeling",
      title: "Renovation And Remodeling",
      description:
        "Structural improvements and interior upgrades for existing homes, offices, and business spaces.",
    },
    {
      slug: "project-management",
      title: "Project Management",
      description:
        "End-to-end supervision with budgeting support, vendor coordination, and quality checks.",
    },
    {
      slug: "maintenance-support",
      title: "Maintenance Support",
      description:
        "Repair and maintenance support that helps properties stay safe, functional, and well maintained.",
    },
  ].map((service) => {
    const detail = serviceGalleryDetails.find(
      (item) => item.slug === service.slug,
    );

    return {
      ...service,
      summary: detail?.summary ?? service.description,
      serviceNote: serviceDescriptions[service.slug] ?? "",
      image: detail?.afterImages[0]?.src ?? detail?.beforeImages[0]?.src ?? "",
      imageAlt:
        detail?.afterImages[0]?.alt ??
        detail?.beforeImages[0]?.alt ??
        service.title,
      highlights: serviceHighlights[service.slug] ?? [],
    };
  });

  return (
    <section className='services-page website-page'>
      <div className='services-hero website-page-hero py-5'>
        <div className='container text-center'>
          <p className='website-page-kicker mb-2'>Our Services</p>
          <h2 className='services-title website-page-title mb-3'>
            Construction services built around planning, quality, and delivery.
          </h2>
          <p className='services-subtitle website-page-subtitle mx-auto mb-0'>
            Construction services shaped around reliable execution, clear
            communication, and practical results for real project needs.
          </p>
        </div>
      </div>
      <div className='container website-page-content'>
        <div className='services-page-section-header text-center'>
          <span className='website-section-kicker'>Service Categories</span>
          <h2 className='website-section-title mb-2'>
            Service details in one place for easier understanding.
          </h2>
          <p className='website-section-subtitle mb-0'>
            Review each service with a clear summary, one reference image, and
            practical highlights before you speak with our team.
          </p>
        </div>
        <div className='services-detail-stack'>
          {services.map((service) => (
            <article className='service-detail-section' key={service.slug}>
              <div className='service-detail-grid'>
                <div className='service-detail-media'>
                  <img src={service.image} alt={service.imageAlt} />
                </div>

                <div className='service-detail-content'>
                  <span className='service-detail-kicker'>Our Service</span>
                  <h3>{service.title}</h3>
                  <p className='service-detail-intro'>{service.description}</p>
                  <p className='service-detail-summary'>{service.summary}</p>
                  <p className='service-detail-note'>{service.serviceNote}</p>

                  <ul className='service-detail-list'>
                    {service.highlights.map((item) => (
                      <li key={`${service.slug}-${item}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className='services-page-section-header text-center mt-3'>
          <span className='website-section-kicker'>How We Deliver</span>
          <h2 className='website-section-title mb-2'>
            A clear step-by-step process followed across all services.
          </h2>
          <p className='website-section-subtitle mb-0'>
            We keep the same practical workflow across construction, upgrades,
            supervision, and maintenance work.
          </p>
        </div>
        <div className='service-process-flow'>
          {commonSteps.map((step, index) => (
            <React.Fragment key={step.step}>
              <div className='service-process-item'>
                <div className='service-process-badge'>{step.step}</div>
                <article className='service-process-step'>
                  <h4>{step.title}</h4>
                  <p className='mb-0'>{step.description}</p>
                </article>
              </div>
              {index < commonSteps.length - 1 && (
                <div className='service-process-connector' aria-hidden='true' />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className='row g-4 align-items-stretch mt-3'>
          <div className='col-lg-6'>
            <div className='service-process-card h-100'>
              <h4 className='mb-3'>How We Work</h4>
              <ul className='list-unstyled mb-0'>
                <li className='process-item'>
                  <span className='process-index'>1</span>
                  <span>Clear requirement review before execution begins</span>
                </li>
                <li className='process-item'>
                  <span className='process-index'>2</span>
                  <span>Planned workflow with supervision at each stage</span>
                </li>
                <li className='process-item'>
                  <span className='process-index'>3</span>
                  <span>
                    Quality checks during execution and before handover
                  </span>
                </li>
                <li className='process-item'>
                  <span className='process-index'>4</span>
                  <span>
                    Practical communication throughout the service cycle
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className='col-lg-6'>
            <div className='service-highlight-card h-100'>
              <h4 className='mb-3'>Why Choose Dialin24</h4>
              <p>
                We combine careful planning, experienced supervision, and clear
                communication to keep work organized from start to finish.
              </p>
              <ul className='mb-0'>
                <li>Clear project coordination and stage-wise follow-up</li>
                <li>Material and workmanship checks during execution</li>
                <li>Visible progress updates and practical timelines</li>
              </ul>
              <a
                href={websitePaths.contact}
                className='btn brand-btn-primary mt-4'
              >
                Get Service Consultation
              </a>
            </div>
          </div>
        </div>
        <div className='service-consult-strip mt-3'>
          <div>
            <span className='website-section-kicker'>Need A Custom Scope?</span>
            <h5 className='mb-2'>
              Tell us what you need and we will guide you to the right service.
            </h5>
            <p className='mb-0'>
              Ideal for new construction, upgrades, repairs, and multi-stage
              project requirements.
            </p>
          </div>
          <a href={websitePaths.contact} className='btn brand-btn-primary'>
            Talk To Our Team
          </a>
        </div>
      </div>
    </section>
  );
};
export default ServicesPage;
